import React, { useCallback, useRef } from 'react'
import PropTypes from 'prop-types'
import { classNames, useSetState } from '@zambezi/sdk/common'

/**
 * Create a React Pager Form component
 *
 * @param {import('./interfaces').PagerFormProps} props
 * @returns {JSX.Element}
 */
export default function PagerForm ({
  hasMax,
  total,
  pageSize,
  onSubmit,
  anchorHrefFn
}) {
  const [state, setState] = useSetState({
    isError: false,
    currentOffset: null
  })
  const formInput = useRef(null)

  const max = hasMax
    ? Math.max(0, Math.floor((total - 1) / pageSize)) + 1
    : null
  const { isError, currentOffset } = state

  const handleChange = useCallback(
    ({ target: { value } }) => {
      const parsedValue = parseInt(value, 10)
      if (
        isNaN(parsedValue) ||
        parsedValue.toString() !== value ||
        parsedValue < 1 ||
        (hasMax && parsedValue > max)
      ) {
        setState({ isError: true, currentOffset: null })
      } else {
        setState({
          isError: false,
          currentOffset: (parsedValue - 1) * pageSize
        })
      }
    },
    [max, hasMax, pageSize]
  )
  const handleSubmit = useCallback(
    event => {
      if (event.type !== 'keydown' || event.key === 'Enter') {
        formInput.current.value = ''
        if (currentOffset === null) {
          setState({ isError: true })
        } else {
          setState({ isError: false })
          onSubmit(currentOffset, event)
        }
      }
    },
    [currentOffset, onSubmit]
  )

  const isDisabled = !total
  const anchorHref =
    currentOffset != null && !isDisabled
      ? anchorHrefFn(currentOffset)
      : undefined
  const isButton = anchorHref == null
  const ButtonTag = isButton ? 'button' : 'a'
  const maximum = hasMax ? ` between 1 and ${max}` : ''
  return (
    <span className='zb-pager-form'>
      <input
        placeholder='Page'
        type='number'
        name='page'
        min='1'
        max={max}
        step='1'
        aria-invalid={isError}
        className={classNames('zb-input', { 'zb-input-is-error': isError })}
        title={isError ? `Enter a page number${maximum}` : 'Page'}
        style={{ minWidth: 120, marginRight: 8, marginLeft: 16 }}
        onChange={handleChange}
        onKeyDown={handleSubmit}
        disabled={isDisabled}
        ref={formInput}
      />
      <ButtonTag
        href={anchorHref}
        className='zb-button zb-button-secondary'
        onClick={handleSubmit}
        style={{ minWidth: 24 }}
        type={isButton ? ButtonTag : undefined}
        disabled={isDisabled}
      >
        Go
      </ButtonTag>
    </span>
  )
}

PagerForm.propTypes = {
  hasMax: PropTypes.bool,
  total: PropTypes.number,
  pageSize: PropTypes.number,
  onSubmit: PropTypes.func,
  anchorHrefFn: PropTypes.func
}
